/**
 * iter106 — Guest document preview card.
 *
 * Renders the guest's Aadhaar thumbnail + view/download/audit actions for any
 * booking that has a `guest_document_id`. Reusable in:
 *   • Voucher modal (Calendar)
 *   • Walk-in detail / BookingDetailScreen
 *   • BFR admin booking detail (read-only)
 *
 * Auto-fetches the active document for the booking and surfaces:
 *   • Thumbnail (token-gated <img>)
 *   • "View Full Image" → opens the signed full-size URL in a new tab
 *   • "Download" → forces save-as
 *   • Audit trail (uploader, uploaded_at, IP, device, hash, view history)
 */
import React, { useEffect, useState } from 'react';
import {
  ShieldCheck, ExternalLink, Download, Clock, Eye, Loader2, FileImage,
  Monitor, MapPin,
} from 'lucide-react';
import { guestDocumentAPI } from '../services/api';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN'); } catch { return iso; }
};

// iter122 — Human-readable labels for the 5 supported document types.
const DOC_LABELS = {
  AADHAAR: 'Aadhaar Card',
  PAN: 'PAN Card',
  PASSPORT: 'Passport',
  DRIVING_LICENSE: 'Driving License',
  VOTER_ID: 'Voter ID Card',
};

const GuestDocumentCard = ({ bookingId, readOnly = false }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAudit, setShowAudit] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const res = await guestDocumentAPI.getForBooking(bookingId);
        const payload = res?.data?.RESULT || res?.RESULT;
        if (!cancelled) setDoc(payload);
      } catch (e) {
        if (!cancelled) setDoc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (bookingId) load();
    return () => { cancelled = true; };
  }, [bookingId]);

  const loadAudit = async () => {
    if (!doc?.id) return;
    setAuditLoading(true);
    try {
      const res = await guestDocumentAPI.audit(doc.id);
      setAuditRows(res?.data?.RESULT || res?.RESULT || []);
    } finally {
      setAuditLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading guest documents…
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="p-3 rounded-lg border border-dashed border-slate-200 text-sm text-slate-500 flex items-center gap-2" data-testid="guest-document-empty">
        <FileImage className="w-4 h-4" />
        No Identity Document Uploaded
      </div>
    );
  }

  const docLabel = DOC_LABELS[doc.document_type] || DOC_LABELS.AADHAAR;
  const downloadName = `${(doc.document_type || 'AADHAAR').toLowerCase()}-${bookingId}.jpg`;

  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 overflow-hidden" data-testid={`guest-document-card-${bookingId}`}>
      <div className="px-3 py-2 border-b border-emerald-100 flex items-center justify-between bg-emerald-50">
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
          <ShieldCheck className="w-4 h-4" />
          Guest Identity Document — <span data-testid="guest-document-type-label">{docLabel}</span>
          {doc.verification_status === 'VERIFIED' && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-200 text-emerald-900">Verified</span>
          )}
          {doc.verification_status === 'UPLOADED' && (
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-slate-200 text-slate-700">Uploaded</span>
          )}
        </div>
        {readOnly && <span className="text-[10px] text-slate-500 uppercase tracking-wide">Read-only</span>}
      </div>

      <div className="p-3 flex gap-3">
        <a
          href={doc.document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block flex-shrink-0 rounded-lg overflow-hidden border border-emerald-100 bg-white"
          data-testid="guest-document-thumbnail-link"
        >
          <img
            src={doc.thumbnail_url}
            alt={`${docLabel} thumbnail`}
            className="w-28 h-20 object-cover"
            data-testid="guest-document-thumbnail"
          />
        </a>
        <div className="flex-1 min-w-0 text-xs text-slate-700 space-y-1">
          <div className="flex items-center gap-2">
            <Clock className="w-3 h-3 text-slate-400" />
            <span>Uploaded {formatDateTime(doc.uploaded_at)}</span>
          </div>
          <div className="truncate">By: <span className="font-medium text-slate-800">{doc.uploaded_by || '—'}</span></div>
          <div className="flex flex-wrap gap-2 pt-1">
            <a
              href={doc.document_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="guest-document-view-btn"
            >
              <ExternalLink className="w-3 h-3" /> View
            </a>
            <a
              href={doc.document_url}
              download={downloadName}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="guest-document-download-btn"
            >
              <Download className="w-3 h-3" /> Download
            </a>
            <button
              onClick={() => { setShowAudit((s) => !s); if (!showAudit && auditRows.length === 0) loadAudit(); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="guest-document-audit-toggle"
            >
              <Eye className="w-3 h-3" /> {showAudit ? 'Hide' : 'Audit'}
            </button>
          </div>
        </div>
      </div>

      {showAudit && (
        <div className="px-3 pb-3" data-testid="guest-document-audit-trail">
          {auditLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading audit trail…</div>
          ) : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto bg-white rounded-lg border border-slate-100 p-2">
              {auditRows.length === 0 && <div className="text-xs text-slate-500">No audit events.</div>}
              {auditRows.map((row, i) => (
                <div key={i} className="text-[11px] text-slate-600 flex items-start gap-2 border-b border-slate-50 last:border-b-0 pb-1">
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 font-mono uppercase">{row.action}</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium text-slate-800">{row.by} <span className="text-slate-400">({row.by_role})</span></div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500 flex-wrap">
                      <span><Clock className="w-2.5 h-2.5 inline" /> {formatDateTime(row.at)}</span>
                      {row.ip && <span><MapPin className="w-2.5 h-2.5 inline" /> {row.ip}</span>}
                      {row.device && <span className="truncate max-w-[200px]"><Monitor className="w-2.5 h-2.5 inline" /> {row.device}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GuestDocumentCard;
